import { AlertTriangle, RotateCw, Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'

interface ErrorFallbackProps {
  error?: Error
}

/**
 * Global error boundary fallback. Replaces the bare `<p>An unexpected error
 * occurred.</p>` so users land on a localized, recoverable surface instead of
 * a raw English string with no chrome and no retry. Sentry still reports the
 * underlying error via the wrapping `Sentry.ErrorBoundary` in main.tsx.
 *
 * In dev / staging, surfaces the message text so engineers can copy/paste it
 * into a bug report. Hidden in production where Sentry has the stack trace.
 */
export function ErrorFallback({ error }: ErrorFallbackProps) {
  const { t } = useTranslation('common')

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
      <Card className="shadow-xl max-w-md w-full" bodyClassName="items-center text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-error" aria-hidden="true" />
          </div>
          <h2 className="card-title">{t('errors.app_crashed_title')}</h2>
          <p className="text-base-content/70">{t('errors.app_crashed_body')}</p>

          {import.meta.env.DEV && error?.message && (
            <pre className="w-full text-xs text-left bg-base-200 p-3 rounded overflow-x-auto">
              {error.message}
            </pre>
          )}

          <div className="card-actions flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              type="button"
              leftIcon={<RotateCw className="w-4 h-4" aria-hidden="true" />}
              onClick={() => window.location.reload()}
            >
              {t('errors.app_crashed_retry')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              leftIcon={<Home className="w-4 h-4" aria-hidden="true" />}
              onClick={() => {
                window.location.href = '/'
              }}
            >
              {t('errors.app_crashed_home')}
            </Button>
          </div>
      </Card>
    </div>
  )
}
